import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  Chip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  ArrowForward,
  CloudUpload,
  Delete,
  Edit,
  PhotoLibrary,
  Search,
  OpenInNew,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/dateUtils';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DetailDialog from '../components/DetailDialog';
import { getEntityDraftFields } from '../lib/entityForms';

const BUCKET = 'gallery';

interface Album {
  id: string;
  name: string;
  cover_image_url: string | null;
  source_url: string | null;
  source: string | null;
  is_public: boolean | null;
  created_at: string;
  photoCount?: number;
  referencedNews?: Array<{ id: string; title: string }>;
}

interface Photo {
  id: string;
  album_id: string | null;
  image_url: string;
  thumb_url: string | null;
  source_url: string | null;
  source: string | null;
  is_public: boolean | null;
  source_index: number | null;
  created_at: string;
}

type PublicFilter = 'all' | 'public' | 'private';

const GalleryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const locale = isRTL ? 'ar' : 'en';

  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Album | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [photoFilter, setPhotoFilter] = useState<PublicFilter>('all');

  // Album create/edit dialog
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [albumName, setAlbumName] = useState('');
  const [albumCover, setAlbumCover] = useState<string | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [savingAlbum, setSavingAlbum] = useState(false);

  // Delete dialogs
  const [deleteAlbumId, setDeleteAlbumId] = useState<string | null>(null);
  const [deletePhoto, setDeletePhotoState] = useState<Photo | null>(null);
  // Photo detail / edit
  const [viewingPhoto, setViewingPhoto] = useState<Photo | null>(null);
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);

  const fetchAlbums = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('photo_albums')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const albumsData = (data || []) as Album[];
      // Fetch photo count + referencing news articles per album.
      const enriched = await Promise.all(
        albumsData.map(async (a) => {
          const [{ count }, newsRes] = await Promise.all([
            supabase.from('gallery_photos').select('id', { count: 'exact', head: true }).eq('album_id', a.id),
            supabase.from('news').select('id,title').eq('album_id', a.id),
          ]);
          return { ...a, photoCount: count || 0, referencedNews: newsRes.data || [] };
        }),
      );
      setAlbums(enriched);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPhotos = useCallback(async (albumId: string) => {
    setPhotosLoading(true);
    try {
      const { data, error } = await supabase
        .from('gallery_photos')
        .select('*')
        .eq('album_id', albumId)
        .order('source_index', { ascending: true });
      if (error) throw error;
      setPhotos((data || []) as Photo[]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPhotosLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const openAlbum = (album: Album) => {
    setSelected(album);
    setPhotoFilter('all');
    fetchPhotos(album.id);
  };

  const uploadToBucket = async (file: File, prefix: string) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${prefix}/${Date.now()}_${safeName}`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { upsert: true, cacheControl: '3600' });
    if (upErr) throw upErr;
    return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
  };

  // ---- Album create/edit ----
  const openAlbumDialog = (album?: Album) => {
    setEditingAlbum(album || null);
    setAlbumName(album?.name || '');
    setAlbumCover(album?.cover_image_url || null);
    setAlbumDialogOpen(true);
    setError('');
  };

  const handleCoverUpload = async (file: File) => {
    setCoverUploading(true);
    setError('');
    try {
      const url = await uploadToBucket(file, 'covers');
      setAlbumCover(url);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCoverUploading(false);
    }
  };

  const saveAlbum = async () => {
    if (!albumName.trim()) return;
    setSavingAlbum(true);
    setError('');
    try {
      if (editingAlbum) {
        const { error } = await supabase
          .from('photo_albums')
          .update({ name: albumName.trim(), cover_image_url: albumCover })
          .eq('id', editingAlbum.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('photo_albums')
          .insert([{ name: albumName.trim(), cover_image_url: albumCover }]);
        if (error) throw error;
      }
      setAlbumDialogOpen(false);
      await fetchAlbums();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingAlbum(false);
    }
  };

  // ---- Photo upload ----
  const handlePhotosUpload = async (files: FileList) => {
    if (!selected) return;
    setUploading(true);
    setError('');
    try {
      const rows: any[] = [];
      for (const file of Array.from(files)) {
        const url = await uploadToBucket(file, selected.id);
        rows.push({ album_id: selected.id, image_url: url });
      }
      if (rows.length) {
        const { error } = await supabase.from('gallery_photos').insert(rows);
        if (error) throw error;
      }
      await fetchPhotos(selected.id);
      await fetchAlbums();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // ---- Rendering ----
  const cardGrid = {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, 1fr)',
      sm: 'repeat(3, 1fr)',
      md: 'repeat(4, 1fr)',
      lg: 'repeat(5, 1fr)',
    },
    gap: 2,
  } as const;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredAlbums = albums.filter((a) =>
    a.name.toLowerCase().includes(search.trim().toLowerCase()));

  const filteredPhotos = photos.filter((p) =>
    photoFilter === 'all' ? true : photoFilter === 'public' ? !!p.is_public : !p.is_public);

  // Album detail view (photos inside one album)
  if (selected) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={() => setSelected(null)} size="small">
            {isRTL ? <ArrowForward /> : <ArrowBack />}
          </IconButton>
          <Typography variant="h5" sx={{ flex: 1 }}>{selected.name}</Typography>
          <Button
            component="label"
            variant="contained"
            size="small"
            sx={{ gap: 0.5 }}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload sx={{ fontSize: 18 }} />}
            disabled={uploading}
          >
            {t('gallery.uploadPhotos')}
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={(e) => {
                if (e.target.files?.length) handlePhotosUpload(e.target.files);
                e.target.value = '';
              }}
            />
          </Button>
        </Box>

        {/* Album metadata: source + what it's referenced by */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          {selected.source && <Chip size="small" label={selected.source} variant="outlined" />}
          <Chip
            size="small"
            label={selected.is_public ? t('gallery.isPublic') : t('common.no')}
            color={selected.is_public ? 'success' : 'default'}
            variant="outlined"
          />
          {selected.source_url && (
            <Button size="small" variant="text" startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
              href={selected.source_url} target="_blank" rel="noopener noreferrer">
              {t('gallery.sourceLink')}
            </Button>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {t('gallery.referencedIn')}
          </Typography>
          {!selected.referencedNews || selected.referencedNews.length === 0 ? (
            <Typography variant="caption" color="text.disabled">{t('gallery.notReferenced')}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.referencedNews.map((n) => (
                <Chip key={n.id} size="small" label={n.title} clickable color="primary" variant="outlined"
                  onClick={() => navigate('/news')} />
              ))}
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={photoFilter}
            onChange={(_, v) => v && setPhotoFilter(v)}
          >
            <ToggleButton value="all">{t('gallery.filterAll')}</ToggleButton>
            <ToggleButton value="public">{t('gallery.filterPublic')}</ToggleButton>
            <ToggleButton value="private">{t('gallery.filterPrivate')}</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {filteredPhotos.length} / {photos.length} {t('gallery.photos')}
          </Typography>
        </Box>

        {photosLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : filteredPhotos.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            {t('gallery.noPhotos')}
          </Typography>
        ) : (
          <Box sx={cardGrid}>
            {filteredPhotos.map((p) => (
              <Card key={p.id} sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  image={p.thumb_url || p.image_url}
                  alt=""
                  sx={{ height: 140, objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => setViewingPhoto(p)}
                />
                {p.is_public === false && (
                  <Chip label={t('common.no')} size="small" color="default"
                    sx={{ position: 'absolute', bottom: 4, insetInlineStart: 4, height: 18, fontSize: '0.65rem' }} />
                )}
                <IconButton
                  size="small"
                  onClick={() => setDeletePhotoState(p)}
                  sx={{
                    position: 'absolute', top: 4,
                    insetInlineEnd: 4,
                    bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                    '&:hover': { bgcolor: 'rgba(200,0,0,0.7)' },
                  }}
                >
                  <Delete sx={{ fontSize: 16 }} />
                </IconButton>
              </Card>
            ))}
          </Box>
        )}

        {deletePhoto && (
          <DeleteConfirmDialog
            open={!!deletePhoto}
            onClose={() => setDeletePhotoState(null)}
            tableName="gallery_photos"
            recordId={deletePhoto.id}
            onDelete={() => { fetchPhotos(selected.id); fetchAlbums(); }}
          />
        )}

        {/* Photo detail dialog: full image + metadata + edit */}
        <Dialog open={!!viewingPhoto} onClose={() => setViewingPhoto(null)} maxWidth="sm" fullWidth>
          {viewingPhoto && (
            <>
              <DialogTitle>{t('gallery.photoDetails')}</DialogTitle>
              <DialogContent>
                <Box component="img" src={viewingPhoto.image_url} alt=""
                  sx={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 1, mb: 2, bgcolor: 'action.hover' }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {viewingPhoto.source && <Chip size="small" label={viewingPhoto.source} variant="outlined" />}
                  <Chip size="small" label={viewingPhoto.is_public ? t('gallery.isPublic') : t('common.no')}
                    color={viewingPhoto.is_public ? 'success' : 'default'} variant="outlined" />
                  {viewingPhoto.source_index != null && (
                    <Chip size="small" label={`${t('gallery.index')}: ${viewingPhoto.source_index}`} variant="outlined" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {formatDate(viewingPhoto.created_at, locale, 'medium')}
                </Typography>
                {viewingPhoto.source_url && (
                  <Button size="small" startIcon={<OpenInNew sx={{ fontSize: 14 }} />} sx={{ mt: 1 }}
                    href={viewingPhoto.source_url} target="_blank" rel="noopener noreferrer">
                    {t('gallery.sourceLink')}
                  </Button>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewingPhoto(null)}>{t('dialog.cancel')}</Button>
                <Button
                  variant="contained"
                  startIcon={<Edit sx={{ fontSize: 16 }} />}
                  onClick={() => { setEditingPhotoId(viewingPhoto.id); setViewingPhoto(null); }}
                >
                  {t('table.edit')}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        <DetailDialog
          open={!!editingPhotoId}
          onClose={() => setEditingPhotoId(null)}
          tableName="gallery_photos"
          recordId={editingPhotoId || undefined}
          fields={(getEntityDraftFields('gallery_photos', isRTL) || []) as any}
          onSave={() => { setEditingPhotoId(null); fetchPhotos(selected.id); }}
        />
      </Box>
    );
  }

  // Albums grid view
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">{t('gallery.title')}</Typography>
        <Button variant="contained" size="small" sx={{ gap: 0.5 }} onClick={() => openAlbumDialog()}>
          <Add sx={{ fontSize: 18 }} />{t('gallery.addAlbum')}
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder={t('gallery.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            ),
          },
        }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {filteredAlbums.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          {t('gallery.noAlbums')}
        </Typography>
      ) : (
        <Box sx={cardGrid}>
          {filteredAlbums.map((album) => (
            <Card key={album.id} sx={{ position: 'relative' }}>
              <CardMedia
                onClick={() => openAlbum(album)}
                sx={{ height: 130, cursor: 'pointer', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                {...(album.cover_image_url ? { image: album.cover_image_url } : {})}
              >
                {!album.cover_image_url && <PhotoLibrary sx={{ fontSize: 40, color: 'text.disabled' }} />}
              </CardMedia>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="subtitle2" noWrap title={album.name}>{album.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {album.photoCount} {t('gallery.photos')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {album.is_public === false && (
                    <Chip label={t('common.no')} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                  {!!album.referencedNews?.length && (
                    <Tooltip title={t('gallery.referencedIn')}>
                      <Chip
                        label={`${t('gallery.referencedIn')}: ${album.referencedNews.length}`}
                        size="small" color="primary" variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
              <Box sx={{ position: 'absolute', top: 4, insetInlineEnd: 4, display: 'flex', gap: 0.5 }}>
                <Tooltip title={t('table.edit')}>
                  <IconButton size="small" onClick={() => openAlbumDialog(album)}
                    sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
                    <Edit sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('table.delete')}>
                  <IconButton size="small" onClick={() => setDeleteAlbumId(album.id)}
                    sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: 'white', '&:hover': { bgcolor: 'rgba(200,0,0,0.7)' } }}>
                    <Delete sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Album create/edit dialog */}
      <Dialog open={albumDialogOpen} onClose={() => setAlbumDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAlbum ? t('gallery.editAlbum') : t('gallery.addAlbum')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label={t('gallery.albumName')}
            value={albumName}
            onChange={(e) => setAlbumName(e.target.value)}
            required
            sx={{ mt: 2, mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {albumCover && (
              <Box component="img" src={albumCover} alt=""
                sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
            )}
            <Button component="label" variant="outlined" size="small" sx={{ gap: 0.5 }}
              startIcon={coverUploading ? <CircularProgress size={14} /> : <CloudUpload sx={{ fontSize: 16 }} />}
              disabled={coverUploading}>
              {albumCover ? t('dialog.replace') : t('gallery.uploadCover')}
              <input type="file" hidden accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverUpload(f); e.target.value = ''; }} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlbumDialogOpen(false)} disabled={savingAlbum}>{t('dialog.cancel')}</Button>
          <Button variant="contained" onClick={saveAlbum} disabled={savingAlbum || !albumName.trim()}>
            {savingAlbum ? t('dialog.saving') : t('dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {deleteAlbumId && (
        <DeleteConfirmDialog
          open={!!deleteAlbumId}
          onClose={() => setDeleteAlbumId(null)}
          tableName="photo_albums"
          recordId={deleteAlbumId}
          onDelete={fetchAlbums}
        />
      )}
    </Box>
  );
};

export default GalleryPage;
